<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const isEnabled = ref(false);
const isAdminExempt = ref(false);
const isUpdating = ref(false);

watch(
  currentAccount,
  () => {
    const { require_assignment_for_reply, assignment_reply_exempt_admin } =
      currentAccount.value?.settings || {};
    isEnabled.value = !!require_assignment_for_reply;
    isAdminExempt.value = !!assignment_reply_exempt_admin;
  },
  { deep: true, immediate: true }
);

const toggleSetting = async () => {
  try {
    isUpdating.value = true;
    const payload = {
      require_assignment_for_reply: isEnabled.value,
    };
    // Se desativou o toggle principal, desativa o sub-toggle também
    if (!isEnabled.value) {
      isAdminExempt.value = false;
      payload.assignment_reply_exempt_admin = false;
    }
    await updateAccount(payload);
    useAlert(t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.API.SUCCESS'));
  } catch (error) {
    isEnabled.value = !isEnabled.value;
    useAlert(t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.API.ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const toggleAdminExempt = async () => {
  try {
    isUpdating.value = true;
    await updateAccount({
      assignment_reply_exempt_admin: isAdminExempt.value,
    });
    useAlert(t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.ADMIN_EXEMPT.API.SUCCESS'));
  } catch (error) {
    isAdminExempt.value = !isAdminExempt.value;
    useAlert(t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.ADMIN_EXEMPT.API.ERROR'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.NOTE')"
    tooltip-identifier="settings_require_assignment_reply"
    with-border
  >
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.LABEL') }}
        </span>
        <span class="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {{ t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.HELP') }}
        </span>
      </div>
      <Switch v-model="isEnabled" @change="toggleSetting" />
    </div>

    <!-- Sub-toggle: Admin exempt -->
    <div
      v-if="isEnabled"
      class="flex items-center justify-between mt-4 pt-4 border-t border-slate-100 dark:border-slate-700"
    >
      <div class="flex flex-col">
        <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
          {{ t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.ADMIN_EXEMPT.LABEL') }}
        </span>
        <span class="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {{ t('GENERAL_SETTINGS.FORM.REQUIRE_ASSIGNMENT_REPLY.ADMIN_EXEMPT.HELP') }}
        </span>
      </div>
      <Switch v-model="isAdminExempt" @change="toggleAdminExempt" />
    </div>
  </SectionLayout>
</template>
